import type { RtcConnection } from 'features/sip/interfaces';

export const LocalVideoSendErrorCode = {
  SenderMissing: 'LOCAL_VIDEO_SENDER_MISSING',
  TrackMissing: 'LOCAL_VIDEO_TRACK_MISSING',
  TrackEnded: 'LOCAL_VIDEO_TRACK_ENDED',
} as const;

const recoverableLocalVideoSendErrorCodes = new Set<string>([
  LocalVideoSendErrorCode.TrackMissing,
  LocalVideoSendErrorCode.TrackEnded,
]);

const createLocalVideoSendError = (code: string) => new Error(code);

export const isRecoverableLocalVideoError = (error: unknown): error is Error =>
  error instanceof Error &&
  recoverableLocalVideoSendErrorCodes.has(error.message);

export const getVideoSender = (rtcConnection: RtcConnection): RTCRtpSender => {
  const sender = rtcConnection
    .getSenders()
    .find((candidate) => candidate.track?.kind === 'video');

  if (sender) {
    return sender;
  }

  const transceiverSender = rtcConnection
    .getTransceivers()
    .find(
      (transceiver) =>
        transceiver.sender &&
        (transceiver.sender.track?.kind === 'video' ||
          transceiver.receiver?.track?.kind === 'video')
    )?.sender;

  if (transceiverSender) {
    return transceiverSender;
  }

  throw createLocalVideoSendError(LocalVideoSendErrorCode.SenderMissing);
};

export const ensureSendableVideoTrack = (
  track: MediaStreamTrack | undefined
): MediaStreamTrack => {
  if (!track) {
    throw createLocalVideoSendError(LocalVideoSendErrorCode.TrackMissing);
  }

  if (track.readyState === 'ended') {
    throw createLocalVideoSendError(LocalVideoSendErrorCode.TrackEnded);
  }

  if (!track.enabled) {
    track.enabled = true;
  }

  return track;
};

/**
 * Bias the outgoing video sender for sign-language interpretation:
 * favor frame rate over resolution under congestion, and mark RTP as
 * high network priority (DSCP AF41 on Chromium). Effective sending
 * rate stays bounded by the SDP-negotiated b=AS.
 */
export const applyVideoSendParameters = async (
  sender: RTCRtpSender
): Promise<void> => {
  try {
    const params = sender.getParameters();
    if (!params.encodings?.length) {
      return;
    }

    (
      params as RTCRtpSendParameters & {
        degradationPreference?: RTCDegradationPreference;
      }
    ).degradationPreference = 'maintain-framerate';

    for (const encoding of params.encodings) {
      encoding.networkPriority = 'high';
      encoding.priority = 'high';
    }

    await sender.setParameters(params);
  } catch (error) {
    console.warn(
      'DEBUG: video sender setParameters(maintain-framerate/high-priority) failed',
      error
    );
  }
};

/** Bias the encoder toward temporal smoothness — appropriate for signing. */
export const markVideoTrackAsMotion = (track: MediaStreamTrack): void => {
  if (track.kind !== 'video' || track.contentHint === 'motion') {
    return;
  }

  try {
    track.contentHint = 'motion';
  } catch (error) {
    console.warn('DEBUG: video track contentHint=motion failed', error);
  }
};
